import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, CustomInput, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { getBankDetails, createWithdrawalRequest } from '../../services/firestoreService';
import { formatCurrency, maskAccountNumber, isValidFundPassword } from '../../utils/helpers';
import './WithdrawScreen.css';

const MIN_WITHDRAWAL = 120;
const WITHDRAWAL_TAX = 8; // 8%

export const WithdrawScreen = () => {
    const navigate = useNavigate();
    const { userData, refreshUserData } = useAuth();
    const alert = useAlert();

    const [amount, setAmount] = useState('');
    const [fundPassword, setFundPassword] = useState('');
    const [bankDetails, setBankDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadBankDetails();
    }, []);

    const loadBankDetails = async () => {
        try {
            const data = await getBankDetails();
            setBankDetails(data);
        } catch (error) {
            console.error('Error loading bank details:', error);
        } finally {
            setLoading(false);
        }
    };

    const isWithdrawalOpen = () => {
        const now = new Date();
        const day = now.getDay();
        const hour = now.getHours();
        return day >= 1 && day <= 5 && hour >= 10 && hour < 18;
    };

    const calculateNetAmount = () => {
        const amountNum = parseInt(amount) || 0;
        const tax = (amountNum * WITHDRAWAL_TAX) / 100;
        return amountNum - tax;
    };

    const validateForm = () => {
        const newErrors = {};

        const amountNum = parseInt(amount);
        if (!amount || isNaN(amountNum)) {
            newErrors.amount = 'Please enter a valid amount';
        } else if (amountNum < MIN_WITHDRAWAL) {
            newErrors.amount = `Minimum withdrawal is ${formatCurrency(MIN_WITHDRAWAL)}`;
        } else if (amountNum > (userData?.withdrawBalance || 0)) {
            newErrors.amount = 'Insufficient balance';
        }

        if (!fundPassword) {
            newErrors.fundPassword = 'Fund password is required';
        } else if (!isValidFundPassword(fundPassword)) {
            newErrors.fundPassword = 'Fund password must be 6 digits';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleWithdraw = async () => {
        if (!isWithdrawalOpen()) {
            alert.showError(
                'Withdrawal Closed',
                'Withdrawals are only available Monday to Friday, 10 AM to 6 PM.'
            );
            return;
        }

        if (!bankDetails) {
            alert.showConfirm(
                'Bank Details Required',
                'Please add your bank details to proceed with withdrawal.',
                () => navigate('/bank-details')
            );
            return;
        }

        if (!userData?.fundPassword) {
            alert.showConfirm(
                'Fund Password Required',
                'Please set your fund password in Security settings.',
                () => navigate('/security')
            );
            return;
        }

        if (!validateForm()) return;

        setSubmitting(true);
        try {
            await createWithdrawalRequest(parseInt(amount), bankDetails.id, fundPassword);
            await refreshUserData();
            alert.showSuccess(
                'Request Submitted',
                'Your withdrawal request has been submitted. It will be processed within 24-72 hours.',
                () => navigate(-1)
            );
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to submit withdrawal request');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading..." />;
    }

    return (
        <div className="withdraw-screen">
            <LoadingOverlay visible={submitting} message="Processing..." />

            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">Withdraw</h1>
                <div style={{ width: 40 }}></div>
            </div>

            <div className="withdraw-content">
                {/* Balance Card */}
                <GlassCard className="balance-card">
                    <div className="balance-header">
                        <span className="material-icons balance-icon">account_balance_wallet</span>
                        <span className="balance-label">Available Balance</span>
                    </div>
                    <span className="balance-amount">{formatCurrency(userData?.withdrawBalance || 0)}</span>
                    <span className="min-withdrawal">Minimum withdrawal: {formatCurrency(MIN_WITHDRAWAL)}</span>
                </GlassCard>

                {/* Bank Details Section */}
                <div className="section">
                    <h3 className="section-title">Withdrawal Account</h3>

                    {bankDetails ? (
                        <GlassCard className="bank-card" onClick={() => navigate('/bank-details')}>
                            <div className="bank-header">
                                <span className="material-icons bank-icon">account_balance</span>
                                <span className="bank-name">{bankDetails.bankName}</span>
                            </div>
                            <div className="bank-details-grid">
                                <div className="bank-detail">
                                    <span className="bank-label">Account Holder</span>
                                    <span className="bank-value">{bankDetails.accountHolderName}</span>
                                </div>
                                <div className="bank-detail">
                                    <span className="bank-label">Account Number</span>
                                    <span className="bank-value">{maskAccountNumber(bankDetails.accountNumber)}</span>
                                </div>
                                <div className="bank-detail">
                                    <span className="bank-label">IFSC Code</span>
                                    <span className="bank-value">{bankDetails.ifscCode}</span>
                                </div>
                            </div>
                            <div className="bank-edit-hint">
                                <span className="material-icons">edit</span>
                                Tap to edit
                            </div>
                        </GlassCard>
                    ) : (
                        <GlassCard className="no-bank-card" onClick={() => navigate('/bank-details')}>
                            <span className="material-icons no-bank-icon">account_balance</span>
                            <span className="no-bank-text">No bank account added</span>
                            <GradientButton
                                title="Add Bank Account"
                                onClick={() => navigate('/bank-details')}
                                size="small"
                            />
                        </GlassCard>
                    )}
                </div>

                {/* Amount Input */}
                <div className="section">
                    <CustomInput
                        label="Withdrawal Amount"
                        type="number"
                        placeholder={`Enter amount (Min: ${formatCurrency(MIN_WITHDRAWAL)})`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        leftIcon="currency_rupee"
                        error={errors.amount}
                    />

                    {amount && parseInt(amount) > 0 && (
                        <div className="amount-breakdown">
                            <div className="breakdown-row">
                                <span>Withdrawal Amount</span>
                                <span>{formatCurrency(parseInt(amount))}</span>
                            </div>
                            <div className="breakdown-row tax">
                                <span>Tax ({WITHDRAWAL_TAX}%)</span>
                                <span>- {formatCurrency((parseInt(amount) * WITHDRAWAL_TAX) / 100)}</span>
                            </div>
                            <div className="breakdown-row net">
                                <span>You'll Receive</span>
                                <span>{formatCurrency(calculateNetAmount())}</span>
                            </div>
                        </div>
                    )}

                    <CustomInput
                        label="Fund Password"
                        placeholder="Enter 6-digit fund password"
                        value={fundPassword}
                        onChange={(e) => setFundPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        leftIcon="lock"
                        isPassword
                        error={errors.fundPassword}
                    />
                </div>

                {/* Info Card */}
                <GlassCard className="info-card">
                    <div className="info-row">
                        <span className="material-icons info-icon info">info</span>
                        <span className="info-text">Withdrawals are processed within 24-72 hours</span>
                    </div>
                    <div className="info-row">
                        <span className="material-icons info-icon warning">percent</span>
                        <span className="info-text">Withdrawal tax: {WITHDRAWAL_TAX}%</span>
                    </div>
                    <div className="info-row">
                        <span className="material-icons info-icon">schedule</span>
                        <span className="info-text">Mon-Fri, 10 AM - 6 PM</span>
                    </div>
                </GlassCard>

                {/* Withdrawal Hours Status */}
                <div className={`hours-status ${isWithdrawalOpen() ? 'open' : 'closed'}`}>
                    <span className="material-icons">{isWithdrawalOpen() ? 'check_circle' : 'schedule'}</span>
                    <span>
                        {isWithdrawalOpen()
                            ? 'Withdrawal window is currently open'
                            : 'Withdrawals: Mon-Fri, 10 AM - 6 PM'}
                    </span>
                </div>

                <GradientButton
                    title="Submit Withdrawal"
                    onClick={handleWithdraw}
                    loading={submitting}
                    disabled={!bankDetails || !userData?.fundPassword || !isWithdrawalOpen()}
                />
            </div>
        </div>
    );
};

export default WithdrawScreen;
